import { contains } from "client/Data/searching";
import {
	listTimeZones,
	TimeZone,
	tryGetTimeZone
} from "client/DateAndTime/timezones";
import { createLocalDataSource } from "client/Hooks";
import { getHighlight } from "client/Misc/highlight";
import { generateTrackingId, isNotNilOrEmpty } from "client/Misc/utils";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { getCurrentCulture } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	ComboBox,
	ComboBoxControlError,
	SelectedComboBoxItem,
	SuggestedComboBoxItem
} from "../ComboBox/ComboBox";
import { ControlAttachmentOptions } from "../ControlAttachment";
import { ValidationError } from "../ValidationError/ValidationError";

export type TimeZonePickerProps = {
	value: string | nil;
	onChange: (value: string | null) => void;
	isInvalid?: boolean;
	isDisabled?: boolean;
	trackingId: string;
	attachedOn?: ControlAttachmentOptions;
	placeholder?: string;
	menuTitle: string;
};

export const TimeZonePicker = (props: TimeZonePickerProps): ReactElement => {
	const {
		value,
		onChange,
		trackingId,
		menuTitle,
		...restOfTheProps
	} = props;

	const [searchTerm, setSearchTerm] = useState<string | undefined>();
	const [error, setError] = useState<ComboBoxControlError | undefined>();

	const currentValue = useMemo(
		(): SelectedComboBoxItem<TimeZone> | null => {
			if (!value) {
				return null;
			}

			const timeZoneRetrieval: TryResult<TimeZone> = tryGetTimeZone(value, currentCulture);

			if (timeZoneRetrieval.wasSuccessful) {
				return {
					value: timeZoneRetrieval.result,
					text: formatTimeZone(timeZoneRetrieval.result)
				};
			}

			console.error(`Unable to get information about timezone with id ${value}`);
			return null;
		},
		[value]
	);

	const {
		currentItems
	} = useDataSource(
		timeZoneOptions,
		{
			searchTerm: searchTerm ?? ""
		}
	);

	const suggestedItems = useMemo(
		(): readonly SuggestedComboBoxItem<TimeZone>[] => {
			return currentItems.map((x) => ({
				...x,
				highlight: getHighlight(x.text, searchTerm)
			}));
		},
		[currentItems, searchTerm]
	);

	const changeHandler = useCallback(
		(item: SelectedComboBoxItem<TimeZone> | undefined): void => {
			onChange(
				item?.value
					.identifier ?? null
			);
		},
		[onChange]
	);

	return (
		<>
			<ComboBox
				{...restOfTheProps}
				value={currentValue}
				onChange={changeHandler}
				searchTerm={searchTerm}
				onSearch={setSearchTerm}
				onErrorChange={setError}
				trackingId={trackingId}
				renderSuggestionBox={
					(props, ref) => (
						<ComboBox.SuggestionBox
							baseProps={props}
							ref={ref}
							suggestedItems={suggestedItems}
							menuTitle={menuTitle}
						/>
					)
				}
			/>

			{error && (
				<ValidationError
					message={error.message}
					trackingId={generateTrackingId(trackingId, "__input-error-message")}
				/>
			)}
		</>
	);
};
TimeZonePicker.displayName = "TimeZonePicker";

const useDataSource = createLocalDataSource<SelectedComboBoxItem<TimeZone>>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) => contains(item.text, searchTerm),
		}
	})
	.getHook();

const currentCulture: string = getCurrentCulture();
const timeZoneOptions: readonly SelectedComboBoxItem<TimeZone>[] = sortBy(
	sortBy(
		sortBy(
			listTimeZones(currentCulture),
			tz => tz.mainCity ?? ""
		),
		tz => tz.name
	),
	tz => tz.offsetInMinutes
)
	.map(
		x => ({
			value: x,
			text: formatTimeZone(x)
		})
	);

export function formatTimeZone(timeZone: TimeZone): string {
	return `(${timeZone.offset}) ${timeZone.name}${isNotNilOrEmpty(timeZone.mainCity) ? " - " + timeZone.mainCity : ""}`;
}